
  import { Vue, Component, Prop, Watch, Ref } from "vue-property-decorator";
  import UPDATE_MESSAGE_RECIPIENT from "@/apollo/conversations/UpdateMessageRecipient.gql";

  import InboxMessage from "@/components/inbox/InboxMessageNew.vue";
  import CustomScroll from "@/components/scroll/CustomScroll.vue";
  import MessageSeparatorByDate from "@/components/inbox/MessagesSeparatorByDateNew.vue";
  import { Message } from "@/generated/graphql";
  import moment from "moment";

  interface DateSeparatorArguments {
    index: number;
    message: Message;
    messages: Message[];
  }

  @Component({
    components: {
      InboxMessage,
      MessageSeparatorByDate,
      CustomScroll,
    },
  })
  export default class InboxWindowMessages extends Vue {
    @Prop({ type: Array, required: true }) readonly messages!: Message[];
    @Prop({ type: String, required: true }) readonly conversationId!: string;
    @Prop({ type: String, required: true }) readonly currentUserId!: string;
    @Prop({ type: Boolean, default: false }) readonly isGroupConversation!: boolean;
    @Prop({ type: Boolean, default: false }) readonly noParticipants!: boolean;
    @Prop({ type: String, default: undefined }) readonly uploadingAttachmentId!: string;

    @Ref("outerContainer") readonly outerContainer!: Vue;

    windowInFocus: boolean = true;

    @Watch("messages")
    async onMessagesChange(updatedList: Message[], oldList: Message[]): Promise<void> {
      if (updatedList.length > oldList.length && this.windowInFocus) {
        this.readMessages();
      }
    }

    created() {
      document.addEventListener("visibilitychange", this.onWindowFocusChanged);
    }

    mounted() {
      this.scrollBottom();
      this.readMessages();
    }

    updated() {
      this.scrollBottom();
    }

    beforeDestroy() {
      document.removeEventListener("visibilitychange", this.onWindowFocusChanged);
    }

    onWindowFocusChanged(): void {
      if (document.visibilityState === "visible") {
        this.windowInFocus = true;
        this.readMessages();
        return;
      }

      this.windowInFocus = false;
    }

    isNewMessage(message: Message): boolean {
      return this.currentUserId !== message.sender.id;
    }

    compareMessageDates(messageOne: Message, messageTwo: Message): boolean {
      return moment(messageOne?.created_at).format('YYYY-MM-DD') != moment(messageTwo?.created_at).format('YYYY-MM-DD');
    }

    showDateSeparation(dateSeparatorArgs: DateSeparatorArguments): boolean {
      const { index, message, messages } = dateSeparatorArgs;
      const isFirstMessage = index === (messages.length - 1);
      const isDateDifferent = this.compareMessageDates(message, messages[index + 1]);
      return isFirstMessage || isDateDifferent;
    }

    scrollBottom(): void {
      if (this.outerContainer) {
        this.outerContainer.$el.children[0].scrollTop = this.outerContainer.$el.children[0].scrollHeight;
      }
    }

    private async readMessages(): Promise<void> {
      const unreadMessagesExist = this.messages.some((message) => {
        const newMessageExists = this.isNewMessage(message);
        return newMessageExists && !message.recipient_message?.[0]?.read;
      });

      if (unreadMessagesExist) {
        await this.$apollo.mutate({
          mutation: UPDATE_MESSAGE_RECIPIENT,
          variables: {
            where: {
              _and: [
                { conversation_id: { _eq: this.conversationId } },
                { recipient_id: { _eq: this.currentUserId } },
                { read: { _eq: false}}
              ]
            },
            set: {read: true },
          }
        })
      }
    }
  }
